import { useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Alert, Button } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import {
  adminPasswordChangeSchema,
  toChangeAdminPasswordRequest,
} from "../schema";
import type { AdminPasswordChangeFormValues } from "../schema";
import type { Schemas } from "../../workspace/api.generated";
import type { LoginFeedback } from "./LoginView";

export interface PasswordChangeViewProps {
  readonly error?: (LoginFeedback & { readonly recovery?: "login" }) | null;
  readonly onClearError?: () => void;
  readonly onComplete: (nextPhase: "totp-enrollment" | "operations") => void;
  readonly onReturnToLogin: () => void;
  readonly onSubmit: (
    input: Schemas["ChangeAdminPasswordRequest"],
  ) => Promise<"totp-enrollment" | "operations" | false>;
}

export function PasswordChangeView({
  error,
  onClearError,
  onComplete,
  onReturnToLogin,
  onSubmit,
}: PasswordChangeViewProps) {
  const submitLock = useRef(false);
  const form = useForm<AdminPasswordChangeFormValues>({
    resolver: zodResolver(adminPasswordChangeSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      newPasswordConfirmation: "",
    },
    shouldFocusError: true,
  });
  const submitting = form.formState.isSubmitting || submitLock.current;

  const submit = form.handleSubmit(async (values) => {
    if (submitLock.current) return;
    submitLock.current = true;
    onClearError?.();
    try {
      const nextPhase = await onSubmit(toChangeAdminPasswordRequest(values));
      if (!nextPhase) return;
      form.reset();
      onComplete(nextPhase);
    } finally {
      submitLock.current = false;
    }
  });

  return (
    <>
      <h1>새 비밀번호를 설정해 주세요</h1>
      <p>새 비밀번호를 설정하면 인증 앱 등록 또는 업무 화면으로 바로 이어집니다.</p>
      {error ? (
        <Alert
          action={error.recovery === "login" ? (
            <Button size="small" variant="quiet" onClick={() => {
              form.reset();
              onReturnToLogin();
            }}>
              다시 로그인해 확인
            </Button>
          ) : undefined}
          persistent
          variant="danger"
          title={error.title}
        >
          {error.message}
        </Alert>
      ) : null}
      <form noValidate onSubmit={submit}>
        <HookFormField
          autoComplete="current-password"
          formControl={form.control}
          label="현재 비밀번호"
          maxLength={200}
          name="currentPassword"
          required
          type="password"
        />
        <HookFormField
          autoComplete="new-password"
          description="8~30자이며 영문 대문자, 소문자, 숫자, 특수문자를 모두 포함합니다."
          formControl={form.control}
          label="새 비밀번호"
          maxLength={30}
          name="newPassword"
          required
          type="password"
        />
        <HookFormField
          autoComplete="new-password"
          formControl={form.control}
          label="새 비밀번호 확인"
          maxLength={30}
          name="newPasswordConfirmation"
          required
          type="password"
        />
        <Button
          className="admin-auth__submit"
          disabled={error?.recovery === "login"}
          isLoading={submitting}
          loadingLabel="비밀번호 변경 중"
          size="large"
          type="submit"
        >
          비밀번호 변경
        </Button>
      </form>
    </>
  );
}
